import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { useState, useEffect, useRef, useMemo } from 'react'
import { useAppStore, useServerStore } from '@/store'
import { users } from '@/mocks'

const Backdrop = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  // @ts-expect-error -- web backdrop-filter
  style: { backdropFilter: 'blur(8px)' },
  zIndex: 2000,
  alignItems: 'center',
  paddingTop: 100,
})

const PaletteContainer = styled(View, {
  width: 560,
  maxHeight: 420,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  overflow: 'hidden',
})

const SearchInput = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  paddingHorizontal: 16,
  height: 48,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  gap: 10,
})

const SearchIcon = styled(Text, {
  fontSize: 16,
  color: '$color8',
})

const ResultSection = styled(View, {
  paddingVertical: 4,
})

const SectionLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '700',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  paddingHorizontal: 16,
  paddingVertical: 6,
})

const ResultItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 36,
  paddingHorizontal: 16,
  gap: 10,
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
  variants: {
    selected: {
      true: {
        backgroundColor: '$sidebarHover',
      },
    },
  } as const,
})

const ResultIcon = styled(Text, {
  fontSize: 14,
  width: 20,
  textAlign: 'center',
  color: '$color8',
})

const ResultText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$surfaceForeground',
  flex: 1,
})

const ResultHint = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 11,
  color: '$color8',
})

const KeyHint = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: 16,
  paddingVertical: 8,
  borderTopWidth: 1,
  borderTopColor: '$borderDefault',
  gap: 16,
})

const KeyCombo = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
})

const Key = styled(View, {
  backgroundColor: '$sidebarHover',
  borderRadius: '$borderRadiusSm',
  paddingHorizontal: 5,
  paddingVertical: 1,
  borderWidth: 1,
  borderColor: '$sidebarActive',
})

const KeyText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 10,
  color: '$sidebarMuted',
})

interface SearchResult {
  id: string
  type: 'server' | 'channel' | 'user' | 'action'
  icon: string
  label: string
  hint?: string
  action: () => void
}

export function CommandPalette() {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const { setCommandPaletteOpen, navigateToServer, setActiveChannel, openCreateServerModal, openJoinServerModal } = useAppStore()
  const servers = useServerStore((s) => s.servers)
  const theme = useTheme()

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const results = useMemo(() => {
    const items: SearchResult[] = []
    const q = query.toLowerCase()

    for (const server of Object.values(servers)) {
      if (!q || server.name.toLowerCase().includes(q)) {
        items.push({
          id: `server-${server.id}`,
          type: 'server',
          icon: '◈',
          label: server.name,
          hint: `${server.memberCount} members`,
          action: () => {
            navigateToServer(server.id)
            const firstChannel = server.channels.find(c => c.type === 'text')
            if (firstChannel) setActiveChannel(firstChannel.id)
            setCommandPaletteOpen(false)
          },
        })
      }

      for (const channel of server.channels) {
        if (!q || channel.name.toLowerCase().includes(q)) {
          items.push({
            id: `channel-${channel.id}`,
            type: 'channel',
            icon: channel.type === 'voice' ? '🔊' : '#',
            label: channel.name,
            hint: server.name,
            action: () => {
              navigateToServer(server.id, channel.id)
              setCommandPaletteOpen(false)
            },
          })
        }
      }
    }

    for (const user of Object.values(users)) {
      if (!q || user.displayName.toLowerCase().includes(q) || user.username.toLowerCase().includes(q)) {
        items.push({
          id: `user-${user.id}`,
          type: 'user',
          icon: '👤',
          label: user.displayName,
          hint: `@${user.username}#${user.discriminator}`,
          action: () => setCommandPaletteOpen(false),
        })
      }
    }

    const actions: SearchResult[] = [
      {
        id: 'action-create',
        type: 'action',
        icon: '+',
        label: 'Create Server',
        action: () => { openCreateServerModal(); setCommandPaletteOpen(false) },
      },
      {
        id: 'action-join',
        type: 'action',
        icon: '→',
        label: 'Join Server',
        action: () => { openJoinServerModal(); setCommandPaletteOpen(false) },
      },
      { id: 'action-settings', type: 'action', icon: '⚙', label: 'Settings', action: () => setCommandPaletteOpen(false) },
    ]

    for (const action of actions) {
      if (!q || action.label.toLowerCase().includes(q)) {
        items.push(action)
      }
    }

    return items.slice(0, 15)
  }, [query, servers, navigateToServer, setActiveChannel, setCommandPaletteOpen, openCreateServerModal, openJoinServerModal])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      results[selectedIndex]?.action()
    }
  }

  const grouped = {
    server: results.filter(r => r.type === 'server'),
    channel: results.filter(r => r.type === 'channel'),
    user: results.filter(r => r.type === 'user'),
    action: results.filter(r => r.type === 'action'),
  }

  let flatIndex = 0

  return (
    <Backdrop onPress={() => setCommandPaletteOpen(false)}>
      <PaletteContainer
        onPress={(e: any) => e.stopPropagation()}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowModal?.val || 'none' }}
      >
        <SearchInput>
          <SearchIcon>🔍</SearchIcon>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0) }}
            onKeyDown={handleKeyDown}
            placeholder="Search servers, channels, users..."
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: '$fontBody',
              fontSize: 15,
              width: '100%',
            }}
          />
        </SearchInput>

        <ScrollView maxHeight={320} showsVerticalScrollIndicator={false}>
          {(!query) && (
            <ResultSection>
              <SectionLabel>Recent</SectionLabel>
            </ResultSection>
          )}

          {grouped.server.length > 0 && (
            <ResultSection>
              <SectionLabel>Servers</SectionLabel>
              {grouped.server.map((r) => {
                const idx = flatIndex++
                return (
                  <ResultItem
                    key={r.id}
                    selected={idx === selectedIndex}
                    onPress={r.action}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <ResultIcon>{r.icon}</ResultIcon>
                    <ResultText>{r.label}</ResultText>
                    {r.hint && <ResultHint>{r.hint}</ResultHint>}
                  </ResultItem>
                )
              })}
            </ResultSection>
          )}

          {grouped.channel.length > 0 && (
            <ResultSection>
              <SectionLabel>Channels</SectionLabel>
              {grouped.channel.map((r) => {
                const idx = flatIndex++
                return (
                  <ResultItem
                    key={r.id}
                    selected={idx === selectedIndex}
                    onPress={r.action}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <ResultIcon>{r.icon}</ResultIcon>
                    <ResultText>{r.label}</ResultText>
                    {r.hint && <ResultHint>{r.hint}</ResultHint>}
                  </ResultItem>
                )
              })}
            </ResultSection>
          )}

          {grouped.user.length > 0 && (
            <ResultSection>
              <SectionLabel>Users</SectionLabel>
              {grouped.user.map((r) => {
                const idx = flatIndex++
                return (
                  <ResultItem
                    key={r.id}
                    selected={idx === selectedIndex}
                    onPress={r.action}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <ResultIcon>{r.icon}</ResultIcon>
                    <ResultText>{r.label}</ResultText>
                    {r.hint && <ResultHint>{r.hint}</ResultHint>}
                  </ResultItem>
                )
              })}
            </ResultSection>
          )}

          {grouped.action.length > 0 && (
            <ResultSection>
              <SectionLabel>Actions</SectionLabel>
              {grouped.action.map((r) => {
                const idx = flatIndex++
                return (
                  <ResultItem
                    key={r.id}
                    selected={idx === selectedIndex}
                    onPress={r.action}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <ResultIcon>{r.icon}</ResultIcon>
                    <ResultText>{r.label}</ResultText>
                  </ResultItem>
                )
              })}
            </ResultSection>
          )}
        </ScrollView>

        <KeyHint>
          <KeyCombo>
            <Key><KeyText>↑↓</KeyText></Key>
            <Text fontSize={10} color="$color8" fontFamily="$fontBody">navigate</Text>
          </KeyCombo>
          <KeyCombo>
            <Key><KeyText>↵</KeyText></Key>
            <Text fontSize={10} color="$color8" fontFamily="$fontBody">select</Text>
          </KeyCombo>
          <KeyCombo>
            <Key><KeyText>esc</KeyText></Key>
            <Text fontSize={10} color="$color8" fontFamily="$fontBody">close</Text>
          </KeyCombo>
        </KeyHint>
      </PaletteContainer>
    </Backdrop>
  )
}
